import { cacheExchange, createClient, fetchExchange } from '@urql/core';
import { assertDariceAuthContext, assertGraphqlVariables, parseGraphqlEnvelope, } from './dto';
let configuredUrl;
let configuredApiKey;
let configuredSessionProvider = () => null;
let configuredFetch;
export function configureGraphqlClient(config) {
    configuredUrl = config.appSyncUrl ?? configuredUrl;
    configuredApiKey = config.appSyncApiKey ?? configuredApiKey;
    configuredSessionProvider = config.getSession ?? configuredSessionProvider;
    configuredFetch = config.fetchImpl ?? configuredFetch;
}
export function buildDariceAuthFetchOptions(session) {
    const auth = assertDariceAuthContext(session);
    const headers = {
        Authorization: `Bearer ${auth.jwt}`,
    };
    if (auth.sessionCookie !== undefined) {
        headers.Cookie = `darice-session=${encodeURIComponent(auth.sessionCookie)}`;
    }
    // No credentials mode: the session rides in headers, the darice-session
    // cookie is scoped to .daricestrategies.com and never applies to the
    // AppSync host, and AppSync's wildcard CORS response is rejected by
    // browsers for credentialed cross-origin requests (TX-368).
    return {
        headers,
    };
}
export function createDariceGraphqlClient(config = {}) {
    if (Object.keys(config).length > 0)
        configureGraphqlClient(config);
    const clientOptions = {
        url: appSyncUrl(),
        exchanges: [cacheExchange, fetchExchange],
        fetchOptions: () => {
            const session = assertDariceAuthContext(configuredSessionProvider());
            return buildDariceAuthFetchOptions(session);
        },
    };
    return createClient(configuredFetch ? { ...clientOptions, fetch: configuredFetch } : clientOptions);
}
export async function callMutation(mutation, variables, opts = {}) {
    const requestVariables = assertGraphqlVariables(variables);
    const headers = { 'Content-Type': 'application/json' };
    if (opts.useApiKey) {
        headers['x-api-key'] = appSyncApiKey();
    }
    else {
        const session = assertDariceAuthContext(opts.session ?? configuredSessionProvider());
        Object.assign(headers, buildDariceAuthFetchOptions(session).headers);
    }
    const response = await fetchImpl()(appSyncUrl(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ query: mutation, variables: requestVariables }),
    });
    if (!response.ok)
        throw new Error(`FAIL: AppSync HTTP ${response.status}`);
    const body = parseGraphqlEnvelope(await response.json());
    if (body.errors && body.errors.length > 0) {
        throw new Error(body.errors[0]?.message ?? 'FAIL: AppSync returned an error');
    }
    if (!body.data)
        throw new Error('FAIL: AppSync returned no data');
    return Object.values(body.data)[0];
}
function fetchImpl() {
    return configuredFetch ?? fetch;
}
function appSyncUrl() {
    return configuredUrl ?? requireEnv('VITE_APPSYNC_URL');
}
function appSyncApiKey() {
    return configuredApiKey ?? requireEnv('VITE_APPSYNC_API_KEY');
}
function requireEnv(name) {
    const v = import.meta.env[name];
    if (!v)
        throw new Error(`FAIL: missing ${name}`);
    return v;
}
//# sourceMappingURL=client.js.map